import { format } from "date-fns";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { DATE_DAY_FROM_MONTH } from "@/services/formatters";

const REQUEST_STATE_LABELS: Record<string, string> = {
	waiting_purchase: "Espera Compra",
	waiting_arrival: "Espera Chegada",
	waiting_delivery: "Espera Entrega",
	delivered: "Entregue",
	no_supplier: "Fornecedor não Encontrado",
	unsuppliable: "Sem fornecedor",
};

type MaterialSummary = {
	brand?: string | null;
	model?: string | null;
	description?: string | null;
};

type PurchaseSummary = {
	createdAt?: string | Date | null;
	origin?: string | null;
};

type MaterialRequestEntry = {
	id?: string | number | null;
	quantity?: number | null;
	material?: MaterialSummary | null;
	state?: string | null;
	createdAt?: string | Date | null;
	purchase?: PurchaseSummary | null;
};

type MaterialRequestsTableProps = {
	requests: MaterialRequestEntry[];
};

const buildMaterialLabel = (material?: MaterialSummary | null) => {
	if (!material) {
		return "-";
	}

	const brand = material.brand?.trim() ?? "";
	const model = material.model?.trim() ?? "";
	const description = material.description?.trim() ?? "";
	const parts = [description, brand, model].filter((value) => value.length > 0);
	return parts.join(", ") || "-";
};

const formatRequestState = (value?: string | null) =>
	value ? (REQUEST_STATE_LABELS[value] ?? "Invalido") : "";

const formatDate = (
	value?: string | Date | null,
	pattern = DATE_DAY_FROM_MONTH,
) => {
	if (!value) {
		return "-";
	}

	const date = typeof value === "string" ? new Date(value) : value;
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return format(date, pattern);
};

const estimateDelivery = (purchase?: PurchaseSummary | null) => {
	if (!purchase?.createdAt) {
		return "-";
	}

	const createdAt =
		typeof purchase.createdAt === "string"
			? new Date(purchase.createdAt)
			: purchase.createdAt;
	if (Number.isNaN(createdAt.getTime())) {
		return "-";
	}

	const startDate = new Date(createdAt);
	let endDate: Date;

	if (purchase.origin === "europe") {
		endDate = new Date(createdAt);
		endDate.setDate(endDate.getDate() + 4);
	} else {
		endDate = new Date(createdAt);
		startDate.setDate(startDate.getDate() + 14);
		endDate.setDate(endDate.getDate() + 42);
	}

	const formattedStart = format(startDate, DATE_DAY_FROM_MONTH);
	const formattedEnd = format(endDate, DATE_DAY_FROM_MONTH);

	return `(${formattedStart} - ${formattedEnd})`;
};

export function MaterialRequestsTable({
	requests,
}: MaterialRequestsTableProps) {
	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[70px]">Qtn.</TableHead>
						<TableHead className="w-[250px]">Descrição</TableHead>
						<TableHead>Estado</TableHead>
						<TableHead className="w-[180px] text-center">Data Pedido</TableHead>
						<TableHead className="w-[180px] text-center">Data Compra</TableHead>
						<TableHead className="w-[180px] text-center">Previsão</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{requests.map((request, index) => {
						const rowKey =
							request.id ?? `${request.state ?? "request"}-${index}`;

						return (
							<TableRow key={rowKey}>
								<TableCell>{request.quantity ?? "-"}</TableCell>
								<TableCell>{buildMaterialLabel(request.material)}</TableCell>
								<TableCell>{formatRequestState(request.state)}</TableCell>
								<TableCell className="text-center">
									{formatDate(request.createdAt, "dd 'de' MMMM 'de' yyyy")}
								</TableCell>
								<TableCell className="text-center">
									{request.purchase?.createdAt
										? formatDate(
												request.purchase.createdAt,
												"dd 'de' MMMM 'de' yyyy",
											)
										: "-"}
								</TableCell>
								<TableCell className="text-center">
									{estimateDelivery(request.purchase)}
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
}
